import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect, useMemo } from 'react';

import { CmsLayoutSSRFactory } from '@/src/cms/presentation/ssr/cms-layout-ssr-factory';
import ContactPageView from '@/src/contact/presentation/views/contact-page-view/contact-page-view';
import { BaseLayout } from '@/src/shared/presentation/components/base-layout';
import { PageMetadata } from '@/src/shared/presentation/components/page-metadata';
import { useHreflang } from '@/src/shared/presentation/hooks/use-hreflang';
import { useLanguage } from '@/src/shared/presentation/hooks/use-locale';
import { SsrDataProvider } from '@/src/shared/presentation/providers/ssr-data-provider';
import { SsrDataBuilder } from '@/src/shared/presentation/ssr/ssr-data-builder';
import { i18nNamespaces, parseLocale } from '@/src/shared/utils/i18n';
import metadata from 'public/assets/metadata/contact.json';

import type { PageMetadataProps } from '@/src/shared/presentation/components/page-metadata';
import type { SsrDataPageProps } from '@/src/shared/presentation/view-models/page.props';
import type { GetStaticPropsContext } from 'next';

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const language = parseLocale(locale);

  return {
    props: {
      ...(await serverSideTranslations(language, i18nNamespaces)),
      ssrSerializedData: {
        [CmsLayoutSSRFactory.getKey()]: {}
      },
      isMobile: false
    }
  };
}

export default function ContactPage({ ssrSerializedData, ...props }: SsrDataPageProps) {
  const language = useLanguage();
  const { setHrefLangMap } = useHreflang();
  const pageMetadataProps: PageMetadataProps = useMemo(
    () => ({
      title: metadata.title[language],
      description: metadata.description[language],
      canonicalUrl: metadata.canonical[language],
      hrefLang: metadata.hreflang,
      ogTitle: metadata.ogTitle[language],
      ogDescription: metadata.ogDescription[language],
      ogUrl: metadata.ogUrl[language],
      ogImageUrl: metadata.ogImageUrl[language],
      twitterTitle: metadata.twitterTitle[language],
      twitterDescription: metadata.twitterDescription[language],
      twitterImageUrl: metadata.twitterImageUrl[language],
      twitterImageAlt: metadata.twitterImageAlt[language]
    }),
    [language]
  );

  useEffect(() => {
    setHrefLangMap(pageMetadataProps.hrefLang);
  }, [setHrefLangMap, pageMetadataProps.hrefLang]);

  return (
    <BaseLayout {...props}>
      <SsrDataProvider
        builder={new SsrDataBuilder([new CmsLayoutSSRFactory()])}
        ssrSerializedData={ssrSerializedData}
      >
        <PageMetadata {...pageMetadataProps} />

        <ContactPageView />
      </SsrDataProvider>
    </BaseLayout>
  );
}
